import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney, formatPct } from "@/lib/format";
import DecisionBadge from "@/components/DecisionBadge";
import { AddStockPanel } from "@/pages/AddStock";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { Plus, X } from "lucide-react";
import StatusToggle from "@/components/StatusToggle";
import InlineRank from "@/components/InlineRank";
import { SortChips, toggleSort, sortRows } from "@/components/SortChips";
import { Switch } from "@/components/ui/switch";
import { todayIso } from "@/lib/datetime";
import usePullToRefresh from "@/lib/usePullToRefresh";
import { RefreshCw } from "lucide-react";

function Metric({ label, value, sub, testId, tone = "default" }) {
  const toneCls =
    tone === "invest" ? "text-invest" :
    tone === "warn" ? "text-monitor" :
    tone === "danger" ? "text-ignore" : "text-app";
  return (
    <div
      className="bg-card-1 border border-line rounded-xl p-3 sm:p-6"
      data-testid={testId}
    >
      <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-2 mb-2 sm:mb-3">{label}</div>
      <div className={`font-mono-num text-xl sm:text-3xl font-semibold ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] sm:text-xs text-muted-1 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [realOnly, setRealOnly] = useState(false); // iter14: filter Test stocks
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const dateStr = todayIso();

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get("/portfolio/summary"),
        api.get(`/portfolio/day/${dateStr}`),
      ]);
      setSummary(s.data);
      setDay(d.data);
    } catch (err) {
      toast.error("Erreur de chargement", { description: err?.response?.data?.detail || String(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const ptr = usePullToRefresh(load);

  if (loading || !summary || !day) {
    return <div className="text-muted-1 text-sm">Chargement…</div>;
  }

  const chartData = summary.curves.real.map((r, i) => ({
    date: r.date,
    real: r.value,
    compound: summary.curves.compound[i]?.value,
    equal: summary.curves.equal_weight[i]?.value,
    top3: summary.curves.top3[i]?.value,
  }));

  return (
    <div className="space-y-5 sm:space-y-8 relative">
      {/* iter 28.7 — pull-to-refresh indicator (mobile) */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 z-30 pointer-events-none"
        style={ptr.indicatorStyle}
        data-testid="ptr-indicator"
      >
        <div className="mt-2 px-3 py-1.5 rounded-full bg-card-2 border border-line-strong text-[10px] uppercase tracking-widest text-muted-1 flex items-center gap-1.5">
          <RefreshCw
            size={11}
            className={ptr.refreshing ? "animate-spin" : ""}
            style={{ transform: `rotate(${ptr.progress * 360}deg)`, transition: ptr.refreshing ? undefined : "transform 0.05s linear" }}
          />
          {ptr.refreshing ? "Rafraîchissement…" : ptr.progress >= 1 ? "Relâche pour rafraîchir" : "Tire pour rafraîchir"}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-2 mb-1">Aujourd'hui</div>
          <h1 className="font-display text-xl sm:text-3xl md:text-4xl font-semibold">
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </h1>
        </div>
        <button
          onClick={() => setAddOpen((v) => !v)}
          className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-full btn-primary text-xs sm:text-sm font-medium transition-colors inline-flex items-center gap-1.5 sm:gap-2"
          data-testid={addOpen ? "dashboard-add-close-btn" : "dashboard-add-btn"}
        >
          {addOpen ? <><X size={14} /> Fermer</> : <><Plus size={14} /> Ajouter des actions</>}
        </button>
      </div>

      {addOpen && (
        <div className="bg-card-1 border border-line rounded-xl p-4 sm:p-6 fade-in-up" data-testid="dashboard-add-panel">
          <AddStockPanel
            onSaved={() => {
              setAddOpen(false);
              load();
            }}
          />
        </div>
      )}

      {/* Bento metrics — iter 26: removed Compound + Drawdown, refocused on
           what the user *actually* cares about day-to-day (real capital + P&L). */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
        <Metric
          label="Capital réel"
          value={formatMoney(summary.real_capital)}
          sub={`sur ${formatMoney(summary.starting_capital)} de départ`}
          tone={summary.real_capital >= summary.starting_capital ? "invest" : "default"}
          testId="metric-real-capital"
        />
        <Metric
          label="Cumul réel"
          value={formatMoney((summary.real_capital ?? 0) + (summary.reserve ?? 0) - (summary.starting_capital ?? 0))}
          sub={`${summary.days_traded} jour(s) suivi(s)`}
          tone={((summary.real_capital ?? 0) + (summary.reserve ?? 0) - (summary.starting_capital ?? 0)) >= 0 ? "invest" : "danger"}
          testId="metric-cumul-real"
        />
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-card-1 border border-line rounded-xl p-3 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-2">Courbes</div>
              <h3 className="font-display text-base sm:text-lg mt-1">Comparaison des stratégies</h3>
            </div>
            <div className="flex gap-2 sm:gap-4 text-[10px] sm:text-[11px] flex-wrap">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-0.5 bg-[#00E676]" /> Réel</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-0.5 bg-white" /> Composé</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-0.5 bg-[#A0A0A0]" /> Égal-p.</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-0.5 bg-[#00B0FF]" /> Top 3</span>
            </div>
          </div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="date" stroke="#707070" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#707070" fontSize={10} tickLine={false} axisLine={false} width={40} />
                <Tooltip
                  contentStyle={{ background: "#141414", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#A0A0A0" }}
                />
                <ReferenceLine y={summary.starting_capital} stroke="#333" strokeDasharray="3 3" />
                <Line type="monotone" dataKey="real" stroke="#00E676" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="compound" stroke="#FFFFFF" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="equal" stroke="#A0A0A0" strokeWidth={1} strokeDasharray="4 3" dot={false} />
                <Line type="monotone" dataKey="top3" stroke="#00B0FF" strokeWidth={1.5} strokeDasharray="2 3" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {summary.days_traded < 20 && (
            <p className="mt-3 text-[11px] font-mono-num text-ignore">
              Attention : échantillon insuffisant ({summary.days_traded} jour(s)). Les courbes ne sont pas encore statistiquement significatives.
            </p>
          )}
        </div>
      )}

      {/* Today's stocks */}
      <div>
        <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
          <h2 className="font-display text-base sm:text-xl">
            Actions du jour
            {day.strategy && (
              <span
                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono-num uppercase tracking-widest align-middle"
                style={{
                  background: day.strategy === "filet" ? "rgba(0,176,255,0.15)" : "rgba(0,230,118,0.15)",
                  color: day.strategy === "filet" ? "#00B0FF" : "#00E676",
                  border: `1px solid ${day.strategy === "filet" ? "#00B0FF66" : "#00E67666"}`,
                }}
                data-testid="dashboard-strategy-badge"
              >
                {day.strategy}
              </span>
            )}
            {day.strategy === "filet" && typeof day.filet_threshold === "number" && day.filet_threshold < 60 && (
              <span
                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono-num align-middle text-muted-1 border border-line"
                title="Seuil abaissé pour garantir 60% de titres éligibles"
                data-testid="dashboard-filet-threshold"
              >
                seuil ≥ {day.filet_threshold}
              </span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-1 cursor-pointer select-none" data-testid="dashboard-real-only-label">
              <Switch checked={realOnly} onCheckedChange={setRealOnly} className="scale-75 origin-left" data-testid="dashboard-real-only-switch" />
              <span className={realOnly ? "text-invest" : ""}>Réel uniquement</span>
            </label>
            <div className="text-[10px] sm:text-xs font-mono-num text-muted-1" data-testid="today-count">
              {day.stocks.length} action(s)
            </div>
          </div>
        </div>

        {/* Iter 27 — retroactive comparison card (visible when either current or counterfactual has non-zero pnl, incl. test-only days) */}
        {day.pnl_counterfactual && (day.pnl.pnl !== 0 || day.pnl_counterfactual.pnl !== 0) && (
          <div
            className="mb-3 bg-card-1 border border-line rounded-xl px-4 py-3 flex items-center gap-4 flex-wrap"
            data-testid="dashboard-strategy-comparison"
          >
            <div className="text-[10px] uppercase tracking-widest text-muted-2 shrink-0">Rétroactif</div>
            <div className="text-sm">
              <span className="font-mono-num text-muted-1">Actuel ({day.strategy}) :&nbsp;</span>
              <span className={`font-mono-num font-medium ${day.pnl.pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                {day.pnl.pnl >= 0 ? "+" : ""}{formatMoney(day.pnl.pnl)}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-mono-num text-muted-1">Si {day.counterfactual_strategy} :&nbsp;</span>
              <span className={`font-mono-num font-medium ${day.pnl_counterfactual.pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                {day.pnl_counterfactual.pnl >= 0 ? "+" : ""}{formatMoney(day.pnl_counterfactual.pnl)}
              </span>
            </div>
          </div>
        )}

        {day.stocks.length === 0 ? (
          <div className="bg-card-1 border border-line border-dashed rounded-xl p-12 text-center">
            <p className="text-muted-1 text-sm mb-4">Aucune action suivie aujourd'hui.</p>
            <button
              onClick={() => setAddOpen(true)}
              className="inline-block px-4 py-2 text-sm rounded-full btn-primary transition-colors"
              data-testid="dashboard-empty-add-btn"
            >
              Ajouter la première action
            </button>
          </div>
        ) : (
          <>
            {/* Sort chips — works on mobile and desktop, tri croissant/décroissant sur toutes les colonnes */}
            <div className="mb-2 flex items-center gap-2 flex-wrap">
              <div className="text-[9px] uppercase tracking-widest text-muted-2 shrink-0">Trier</div>
              <SortChips
                testIdPrefix="dashboard-sort"
                sortKey={sort.key}
                sortDir={sort.dir}
                onChange={(k) => setSort((s) => toggleSort(s, k, k === "symbol" ? "asc" : "desc"))}
                options={[
                  { key: "rank", label: "Rang" },
                  { key: "symbol", label: "Symb." },
                  { key: "score", label: "Score" },
                  { key: "alloc", label: "Alloc." },
                  { key: "gain", label: "Gain %" },
                  { key: "pnl", label: "P&L" },
                ]}
              />
            </div>

          <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
            {/* Header — 5 cols on mobile (Gain+P&L merged), 6 cols on sm+ */}
            <div className="grid grid-cols-[32px_1fr_56px_68px_1fr] sm:grid-cols-[44px_1fr_72px_90px_90px_90px] gap-x-3 sm:gap-4 px-3 sm:px-6 py-2 sm:py-3 border-b border-line text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-2">
              <div>#</div><div>Symb.</div><div className="text-right">Score</div>
              <div className="text-right">Alloc.</div>
              <div className="text-right"><span className="sm:hidden">Gain / P&L</span><span className="hidden sm:inline">Gain</span></div>
              <div className="hidden sm:block text-right">P&L</div>
            </div>
            {sortRows(
              day.stocks.filter((s) => !realOnly || (s.status || "test").toLowerCase() === "real"),
              sort.key,
              sort.dir,
              {
                rank: (s) => s.rank_in_list ?? Infinity,
                symbol: (s) => s.symbol,
                score: (s) => s.score ?? -Infinity,
                alloc: (s) => s.effective_allocation ?? -Infinity,
                gain: (s) => day.pnl.detail.find((d) => d.stock_id === s.id)?.gain_pct ?? null,
                pnl: (s) => day.pnl.detail.find((d) => d.stock_id === s.id)?.pnl ?? null,
              }
            ).map((s) => {
              const p = day.pnl.detail.find((d) => d.stock_id === s.id);
              const gainPct = p?.gain_pct;
              const pnl = p?.pnl;
              const alloc = s.effective_allocation;
              return (
                <Link
                  key={s.id}
                  to={`/stock/${s.id}`}
                  className="grid grid-cols-[32px_1fr_56px_68px_1fr] sm:grid-cols-[44px_1fr_72px_90px_90px_90px] gap-x-3 sm:gap-4 px-3 sm:px-6 py-2.5 sm:py-4 border-b border-line hover:bg-card-2 transition-colors items-center"
                  data-testid={`stock-row-${s.symbol}`}
                >
                  <div className="flex items-center"><InlineRank stock={s} onChanged={load} /></div>
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-sm sm:text-base truncate">{s.symbol}</div>
                    <div className="text-[10px] sm:text-xs text-muted-2 truncate hidden sm:block">{s.name || " "}</div>
                    <div className="mt-1"><StatusToggle stock={s} onChanged={load} /></div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num text-sm sm:text-lg font-semibold leading-tight">{s.score}</div>
                    <div className="mt-0.5 sm:mt-1"><DecisionBadge score={s.score} size="sm" /></div>
                  </div>
                  <div className="font-mono-num text-right text-xs sm:text-base">{formatMoney(alloc)}</div>
                  {/* Mobile: Gain% on top, P&L below (stacked). Desktop: two separate columns. */}
                  <div className={`font-mono-num text-right text-sm sm:text-base leading-tight ${gainPct == null ? "text-muted-2" : gainPct >= 0 ? "text-invest" : "text-ignore"}`}>
                    <div className="font-medium">{gainPct == null ? "—" : formatPct(gainPct)}</div>
                    <div className={`sm:hidden text-xs mt-0.5 ${pnl == null ? "text-muted-2" : pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                      {pnl == null ? "—" : formatMoney(pnl)}
                    </div>
                  </div>
                  <div className={`hidden sm:block font-mono-num text-right text-xs sm:text-base ${pnl == null ? "text-muted-2" : pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                    {pnl == null ? "—" : formatMoney(pnl)}
                  </div>
                </Link>
              );
            })}
            <div className="grid grid-cols-[32px_1fr_56px_68px_1fr] sm:grid-cols-[44px_1fr_72px_90px_90px_90px] gap-x-3 sm:gap-4 px-3 sm:px-6 py-2 sm:py-3 text-[10px] sm:text-xs font-mono-num">
              <div></div><div className="text-muted-1">Total Réel</div>
              <div></div>
              <div className="text-right text-muted-1">{formatMoney(day.pnl.total_allocation_real ?? day.pnl.total_allocation)}</div>
              {/* Mobile: total P&L only under merged col. Desktop: keeps two cols. */}
              <div className={`sm:hidden text-right ${day.pnl.pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                {formatMoney(day.pnl.pnl)}
              </div>
              <div className="hidden sm:block"></div>
              <div className={`hidden sm:block text-right ${day.pnl.pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                {formatMoney(day.pnl.pnl)}
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
